import { useState } from "react";
import { Button } from "@/components/ui/button";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { typeOf, toDraft, fromDraft, preview, toPlain } from "@/lib/fieldCodec";
import { TypeSelect, ValueInput } from "./ValueInput";

const TYPE_TONE = { string: "text-emerald-700", number: "text-blue-700", boolean: "text-amber-700", timestamp: "text-violet-700", map: "text-cyan-700", array: "text-cyan-700", null: "text-muted-foreground" };

export function FieldRow({ name, value, canEdit, onSave, onDelete }) {
  const origType = typeOf(value);
  const editable = ["geopoint", "reference"].includes(origType) ? "map" : origType;
  const [editing, setEditing] = useState(false);
  const [type, setType] = useState(editable);
  const [draft, setDraft] = useState("");
  const [expanded, setExpanded] = useState(false);
  const tid = name.replace(/[^a-zA-Z0-9_-]/g, "_");
  const complex = ["map", "array"].includes(origType);

  const start = () => { setType(editable); setDraft(toDraft(value, editable)); setEditing(true); };
  const changeType = (t) => { setType(t); setDraft(toDraft(t === origType ? value : undefined, t)); };
  const save = async () => {
    let next;
    try { next = fromDraft(draft, type); } catch (e) { toast.error(`${name}: ${e.message}`); return; }
    const ok = await onSave(name, next);
    if (ok) setEditing(false);
  };

  return (
    <div className="border-b border-border last:border-0 py-2.5 px-3 hover:bg-accent/30 transition-colors" data-testid={`field-row-${tid}`}>
      <div className="flex items-start gap-3">
        <div className="w-44 shrink-0 min-w-0">
          <p className="font-mono text-xs text-foreground break-all" data-testid={`field-name-${tid}`}>{name}</p>
          <p className={`font-mono text-[10px] ${TYPE_TONE[origType] || "text-muted-foreground"}`}>{origType}</p>
        </div>
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="space-y-2">
              <TypeSelect value={type} onChange={changeType} testId={`field-type-${tid}`} />
              <ValueInput type={type} draft={draft} setDraft={setDraft} testId={`field-input-${tid}`} />
              <div className="flex gap-2">
                <Button size="sm" className="h-7" onClick={save} data-testid={`field-save-${tid}`}><Icons.Check className="h-3.5 w-3.5 mr-1" />Save</Button>
                <Button size="sm" variant="ghost" className="h-7" onClick={() => setEditing(false)} data-testid={`field-cancel-${tid}`}>Cancel</Button>
              </div>
            </div>
          ) : complex ? (
            <button className="text-left w-full" onClick={() => setExpanded((x) => !x)} data-testid={`field-expand-${tid}`}>
              {expanded
                ? <pre className="text-[11px] font-mono bg-secondary/60 rounded p-2 overflow-x-auto max-h-80 whitespace-pre-wrap break-all">{JSON.stringify(toPlain(value), null, 2)}</pre>
                : <p className="text-xs font-mono text-muted-foreground truncate">{preview(value, 120)}</p>}
            </button>
          ) : (
            <p className="text-sm text-foreground break-words whitespace-pre-wrap" data-testid={`field-value-${tid}`}>{preview(value, 600)}</p>
          )}
        </div>
        {canEdit && !editing && (
          <div className="flex shrink-0 gap-1">
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={start} data-testid={`field-edit-${tid}`}><Icons.Pencil className="h-3.5 w-3.5" /></Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => onDelete(name)} data-testid={`field-delete-${tid}`}><Icons.Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        )}
      </div>
    </div>
  );
}
